/**
 * `briefings.*` — anchor procedures for Workspace Home (S08).
 *
 * `briefings.today` is the first real read this skeleton lights up.
 * `briefings.workspaceSignals` aggregates the dashboard's secondary
 * signals (cash breakdown, petty cash, bank-match, TDS, approvals)
 * in one call so the SPA doesn't fan out 5+ small queries.
 */
import { z } from "zod";
import { moneyPaise, signedPaise, isoDate, uuid } from "../_common.js";

export const todayInput = z.object({}).strict();

export const todayOutput = z.object({
  date: isoDate,
  greeting: z.string(),
  cash: z.object({
    /** Cash KPI is the sum of all tracked accounts — signed since cards
     *  flip to liability and a drawer can be negative pre-guard. */
    balancePaise: signedPaise,
    /** WoW delta — naturally signed. */
    deltaPaise: signedPaise,
    confidence: z.enum(["low", "medium", "high"]),
  }),
  receivables: z.object({
    /**
     * Sum of issued + partially_paid + overdue invoice totals. Gross
     * of any partial payments until the receipts vertical (V1.x)
     * lands — the SPA surfaces this caveat via a tooltip on the
     * Receivables card. Same number the `/invoices` page shows as
     * "Outstanding"; both surfaces read from `getInvoiceListKpis`.
     */
    totalPaise: moneyPaise,
    overduePaise: moneyPaise,
    /** Distinct customers with at least one overdue invoice. */
    overdueCustomerCount: z.number().int().nonnegative(),
    /** Count of overdue invoice rows. */
    overdueInvoiceCount: z.number().int().nonnegative(),
  }),
  compliance: z.object({
    nextDeadline: z.string(),
    daysUntil: z.number().int(),
  }),
  topAction: z.object({
    kind: z.string(),
    label: z.string(),
    href: z.string(),
  }),
  briefingLines: z.array(z.string()),
});

export type TodayInput = z.infer<typeof todayInput>;
export type TodayOutput = z.infer<typeof todayOutput>;

// ── workspaceSignals — dashboard secondary tiles ────────────────────

/**
 * Empty input. The procedure is tenant-scoped; the only signal it needs
 * is "which company is asking" which the auth context already carries.
 */
export const workspaceSignalsInput = z.object({}).strict();

/**
 * Aggregated read for the Workspace Home health-signal cards + the
 * Cash KPI's breakdown footer. Composed server-side from existing
 * domain queries (bank-accounts list, statement-imports unmatched,
 * tds quarter summary, approvals list) so the SPA gets one wire roundtrip.
 *
 * Shape is deliberately flat — each card on the dashboard maps to a top-
 * level key — so the SPA can render one block without picking through
 * nested unions.
 */
export const workspaceSignalsOutput = z.object({
  /**
   * Cash KPI tile breakdown footer — sums of active bank accounts grouped
   * by `type`. The card balance is flipped to negative on the wire so the
   * SPA can render it as a liability without re-doing the sign math.
   */
  cashBreakdown: z.object({
    /** Sum across active bank accounts. Signed for safety — a bank can
     *  legitimately overdraft and we don't model that ban. */
    bankPaise: signedPaise,
    /** Sum across cash drawers. Signed: a drawer can be negative for
     *  legacy rows predating the over-balance guard (commit cdc5916). */
    cashPaise: signedPaise,
    /** Card liability flipped to negative on the wire so the SPA renders
     *  "−₹X" without re-doing the sign math. */
    cardPaise: signedPaise,
  }),
  /**
   * The petty-cash imprest card. `null` drawerId means there's no
   * type=cash account on file — the SPA renders the "Set up a drawer"
   * empty state. `imprestPaise` of 0 means a drawer exists but no imprest
   * ceiling was set; the SPA shows balance only (no bar).
   *
   * `lastCountedAt` / `daysSinceLastCount` are V1.x-reserved — there's no
   * `cash_counts` table yet, so they're always null in V1.0.x. Kept in
   * the contract so the SPA layout doesn't churn when the count vertical
   * lands.
   */
  pettyCash: z.object({
    drawerId: z.string().nullable(),
    drawerName: z.string().nullable(),
    /** Running drawer balance — signed for parity with pettyCash.anchor
     *  Data (legacy over-balance rows can produce a negative). */
    balancePaise: signedPaise,
    imprestPaise: moneyPaise,
    lastCountedAt: z.string().nullable(),
    daysSinceLastCount: z.number().int().nonnegative().nullable(),
  }),
  /**
   * Statement-import exceptions across all active bank accounts. The card
   * deep-links to /reconcile; the name + ID surface the most relevant
   * account when more than one has unmatched rows.
   */
  bankMatch: z.object({
    unmatchedCount: z.number().int().nonnegative(),
    accountsWithUnmatched: z.number().int().nonnegative(),
    primaryAccountId: z.string().nullable(),
    primaryAccountName: z.string().nullable(),
    oldestUnmatchedDate: z.string().nullable(),
  }),
  /**
   * TDS deposit due for the current Indian-FY quarter. `daysUntilDue` is
   * the signed gap to the 26Q return date (negative = past due). Sum is
   * the quarter's collected TDS — the actual deposit amount once the
   * challan-281 write-side lands (V1.x); for now it's the read-only
   * sum from `vendor_payments.tds_paise`.
   */
  tds: z.object({
    depositDuePaise: moneyPaise,
    quarter: z.string(),
    dueOn: isoDate,
    daysUntilDue: z.number().int(),
  }),
  /**
   * Founder approvals queue. `totalAtStakePaise` sums `grossPaise` from
   * each pending row's `requestPayload` (the only approval kind that
   * carries a money amount today is `vendor_payment_above_threshold`).
   */
  approvals: z.object({
    pendingCount: z.number().int().nonnegative(),
    totalAtStakePaise: moneyPaise,
  }),
});

export type WorkspaceSignalsInput = z.infer<typeof workspaceSignalsInput>;
export type WorkspaceSignalsOutput = z.infer<typeof workspaceSignalsOutput>;

// ── cashTrend — Workspace Home 30-day cash sparkline ────────────────

/**
 * Window length for the cash trend. The dashboard's segmented control
 * offers 7 / 30 / 90 days; 30 is the default (the labelled "last 30 days"
 * view). Defaulted so the SPA can call with `{}` for the headline view.
 */
export const cashTrendInput = z
  .object({
    days: z.union([z.literal(7), z.literal(30), z.literal(90)]).default(30),
  })
  .strict();

export const cashTrendPoint = z.object({
  date: isoDate,
  /** Closing cash position at end of day — signed (card is a liability). */
  balancePaise: signedPaise,
});

/**
 * Daily closing cash position over the trailing window, plus the summary
 * the chart caption shows (today, net change vs window start, low, high).
 *
 * `points` is ascending by date with exactly `days` entries; the final
 * point is today and its `balancePaise` equals the Cash KPI tile to the
 * paise (both derive from the same per-account running balances).
 */
export const cashTrendOutput = z.object({
  days: z.number().int().positive(),
  points: z.array(cashTrendPoint),
  todayPaise: signedPaise,
  openingPaise: signedPaise,
  netChangePaise: signedPaise,
  lowPaise: signedPaise,
  highPaise: signedPaise,
});

export type CashTrendInput = z.infer<typeof cashTrendInput>;
export type CashTrendOutput = z.infer<typeof cashTrendOutput>;

export const cashRunwayInput = z.object({}).strict();

/**
 * Cash-runway summary for the Workspace Home hero ("~4 months at your usual
 * spending"). `runwayMonths` is null when there's no measurable spend to
 * divide by; `confidence` lets the UI hedge thin-history estimates.
 */
export const cashRunwayOutput = z.object({
  cashOnHandPaise: signedPaise,
  avgMonthlyOutflowPaise: moneyPaise,
  runwayMonths: z.number().nonnegative().nullable(),
  confidence: z.enum(["low", "medium"]),
});

export type CashRunwayInput = z.infer<typeof cashRunwayInput>;
export type CashRunwayOutput = z.infer<typeof cashRunwayOutput>;

export const askInput = z.object({
  q: z.string().min(1).max(280),
  /** Optional structured context; omitted for free-form questions. */
  context: z.record(z.string()).optional(),
});

export const askOutput = z.object({
  answerMarkdown: z.string(),
  citations: z.array(
    z.object({
      label: z.string(),
      entityType: z.string(),
      entityId: uuid,
    }),
  ),
  /** Confidence and model metadata so UI can surface "show me the data". */
  meta: z.object({
    model: z.string(),
    modelVersion: z.string(),
    confidence: z.number().min(0).max(1),
    promptId: uuid,
  }),
});
